import { AppBanner } from "@circle-react/components/AppBanner";
import { useSettingsHeader } from "@circle-react/components/shared/uikit/HeaderV3/useSettingsHeader";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useSpacesContext } from "@circle-react/contexts/spacesContext";
import {
  isPromoCodeEnabled,
  isSubscriptionExpired,
} from "@circle-react/helpers/communityHelpers";
import { shouldRenderBillingAfterCheckout } from "@circle-react/helpers/plansHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useMasqueradeEnabledAndActive } from "@circle-react/hooks/useMasqueradeEnabledAndActive";
import { useIsMobile } from "@circleco/compass/hooks/useIsMobile";
import { useParams } from "@tanstack/react-router";
import type { ReactNode } from "react";

import {
  useIsInsideCircleAIShell,
  useShellBypass,
} from "@/react/components/Layout/CircleAIAppShellLayout/ShellBypassContext";
import { Body } from "@/react/components/Layout/StandardLayoutV2/Body";
import { Breadcrumb } from "@/react/components/Layout/StandardLayoutV2/Breadcrumb";
import { Content } from "@/react/components/Layout/StandardLayoutV2/Content";
import { CoverImage } from "@/react/components/Layout/StandardLayoutV2/CoverImage";
import { Header } from "@/react/components/Layout/StandardLayoutV2/Header";
import { Right } from "@/react/components/Layout/StandardLayoutV2/Right";
import { useIsBgPrimarySpace } from "@/react/components/Layout/useIsBgPrimarySpace";
import { MobileLayoutShell } from "@/react/components/Mobile/MobileLayoutShell";
import { SideBar } from "@/react/components/Spaces/Sidebar";
import { isSafariMontereyOrOlder } from "@/react/helpers/browserHelpers";
import {
  anchorClassNames,
  legacyClassNames,
} from "@/react/helpers/cssAnchorHelpers";
import { usePostListPinnedOnSidebar } from "@/react/hooks/posts/usePostList";
import { useAppendBodyClasses } from "@/react/hooks/useAppendBodyClasses";
import { useRouterQueryParams } from "@/react/hooks/useRouterQueryParams";
import { useSpaceSlugFromUrl } from "@/react/hooks/useSpaceSlugFromUrl";
import { useElectronAPI } from "@/react/hooks/utils/useElectronAPI";
import { useRouteHelpers } from "@/react/routes/Main/useRouteHelpers";

import { AppBar } from "../AppBar";
import { MobileDrawerProvider } from "../MobileDrawer/mobileDrawerContext";
import { CommunityContent } from "./CommunityContent";
import { CommunityWrapper } from "./CommunityWrapper";
import { ContentWrapper } from "./ContentWrapper";
import { PlanTrialTopBanner } from "./PlanTrialTopBanner";
import { RailbarContainer } from "./RailbarContainer";
import { shouldDisableRightSidebar } from "./shouldDisableRightSidebar";
import { SidebarWrapper } from "./SidebarWrapper";
import { StandardLayoutV2Wrapper } from "./StandardLayoutV2Wrapper";

import "./overrides.scss";
import "./styles.scss";
import { TrialBannerV2 } from "./TrialBannerV2";
import { useTopBarClassNames } from "./useTopBarClassNames";

export interface StandardLayoutV2Props {
  bodyClassNames?: string;
  children: ReactNode;
  disableRightSidebar?: boolean;
  disableScroll?: boolean;
  disableSidebar?: boolean;
  hideAppBar?: boolean;
  id?: string;
  renderLeftSidebar?: () => ReactNode;
  shouldHideSidebarNestedNavBar?: boolean;
}

/**
 * Gate component that chooses between the Compass MobileShell and the
 * existing desktop StandardLayoutV2 based on viewport.
 *
 * This is a separate component (not an early return inside
 * StandardLayoutV2Desktop) to avoid violating React's rules of hooks -
 * StandardLayoutV2Desktop has 15+ hooks that must always be called.
 */
const StandardLayoutV2Root = ({ children, ...rest }: StandardLayoutV2Props) => {
  const isMobile = useIsMobile();
  const isInsideAIShell = useIsInsideCircleAIShell();

  if (isMobile) {
    // When inside the CircleAI shell, CircleAIAppShellLayout already
    // renders MobileLayoutShell at the outer level — just pass through
    // children so we don't get duplicate chrome or the legacy desktop
    // header/sidebar rendered inside the mobile shell.
    if (isInsideAIShell) {
      return <>{children}</>;
    }
    // Legacy path (not inside AI shell): render mobile shell here.
    return <MobileLayoutShell>{children}</MobileLayoutShell>;
  }

  return (
    <StandardLayoutV2Desktop {...rest}>{children}</StandardLayoutV2Desktop>
  );
};

const StandardLayoutV2Desktop = ({
  bodyClassNames = "",
  children,
  disableRightSidebar = false,
  disableScroll = false,
  disableSidebar = false,
  hideAppBar = false,
  shouldHideSidebarNestedNavBar = false,
  id,
  renderLeftSidebar,
}: StandardLayoutV2Props) => {
  useShellBypass();
  const isInsideCircleAIShell = useIsInsideCircleAIShell();

  const { currentCommunityMember, currentCommunity } = usePunditUserContext();
  const { posts: pinnedPosts } = usePostListPinnedOnSidebar();
  const isBgPrimarySpace = useIsBgPrimarySpace();
  const containerProps: Record<string, unknown> = { id };
  const isMasqueradingEnabledAndActive = useMasqueradeEnabledAndActive();
  const { isElectron } = useElectronAPI();

  const {
    isSettingsRoute,
    isReactFlowWorkflowsRoute,
    isBotBuilderEditorRoute,
  } = useSettingsHeader();

  const isDvhUnsupportedInSafari = isSafariMontereyOrOlder();

  useAppendBodyClasses(
    classNames(
      anchorClassNames("is-standard-layout-v2 react-standard-layout"),
      legacyClassNames("is-standard-layout-v2--v3-enabled"),
      "bg-secondary",
      bodyClassNames,
      shouldHideSidebarNestedNavBar &&
        legacyClassNames(
          "is-standard-layout-v2--v3-enabled--hidden-nested-links",
        ),
      {
        "safari-dvh-unsupported": isDvhUnsupportedInSafari,
      },
    ),
  );
  const queryParams = useRouterQueryParams();

  const { postSlug } = useParams({ strict: false });

  const { isHomePage } = useRouteHelpers();

  const spaceSlug = useSpaceSlugFromUrl();
  const spaceData = useSpacesContext().helpers.findBy({ slug: spaceSlug });
  const space = spaceData || {};

  const hasSubscriptionExpired =
    isSubscriptionExpired(currentCommunity) &&
    !shouldRenderBillingAfterCheckout(queryParams);
  const isPromoCodeEnabledForCommunity = isPromoCodeEnabled(currentCommunity);

  // TODO: refactor so that we rely on child props to set this value.
  const disableLayoutRightSidebar = shouldDisableRightSidebar({
    disableRightSidebar,
    postSlug,
    space,
    pinnedPosts,
    isHomePage,
    currentCommunityMember,
  });

  const topBarClassNames = useTopBarClassNames(
    isPromoCodeEnabledForCommunity || hasSubscriptionExpired,
    isMasqueradingEnabledAndActive,
  );

  const layoutInner = (
    <CommunityWrapper {...containerProps}>
      {!disableSidebar ? <div id="react-mobile-drawer-root" /> : null}
      <div
        className={classNames(
          "bg-secondary flex w-full flex-col",
          topBarClassNames,
          {
            "h-screen": !disableScroll,
            "h-full": disableScroll,
            "!h-[calc(100vh-105px)]": isElectron && isSettingsRoute,
          },
        )}
        data-testid="standard-layout-v2"
      >
        {!isInsideCircleAIShell && (
          <>
            <PlanTrialTopBanner />
            <TrialBannerV2 />
            <AppBanner />
          </>
        )}
        <CommunityContent disableSidebar={disableSidebar}>
          {!hideAppBar && <AppBar />}
          <StandardLayoutV2Wrapper
            disableSidebar={disableSidebar}
            disableLayoutRightSidebar={disableLayoutRightSidebar}
            isFullHeight={isBotBuilderEditorRoute}
            hasSettingsSidebar={
              !isReactFlowWorkflowsRoute &&
              !isBotBuilderEditorRoute &&
              isSettingsRoute &&
              !hasSubscriptionExpired &&
              !isPromoCodeEnabledForCommunity
            }
            disableSidebarNestedBarLinks={shouldHideSidebarNestedNavBar}
          >
            {!disableSidebar &&
              !isReactFlowWorkflowsRoute &&
              !isBotBuilderEditorRoute && (
                <SidebarWrapper>
                  {renderLeftSidebar ? renderLeftSidebar() : <SideBar />}
                </SidebarWrapper>
              )}
            <ContentWrapper
              isBgSecondary={!isBgPrimarySpace}
              isBgPrimary={!!isBgPrimarySpace}
            >
              {children}
            </ContentWrapper>
            <div className={anchorClassNames("standard-layout-v2__railbar")}>
              <RailbarContainer />
            </div>
            <div id="mobile-dropdown-container" />
          </StandardLayoutV2Wrapper>
        </CommunityContent>
      </div>
    </CommunityWrapper>
  );

  if (disableSidebar) {
    return layoutInner;
  }

  return (
    <MobileDrawerProvider renderLeftSidebar={renderLeftSidebar}>
      {layoutInner}
    </MobileDrawerProvider>
  );
};

/**
 * Public API. The compound object (StandardLayoutV2.Header, .Body, etc.)
 * is attached to the gate component so existing consumers are unaffected.
 */
export const StandardLayoutV2 = Object.assign(StandardLayoutV2Root, {
  Header,
  Breadcrumb,
  Content,
  Body,
  Right,
  CoverImage,
});
